import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { accountUpdate, logout } from "../redux/authSlice";
import axios from "axios";
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from "react-toastify";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { FaKey, FaUserSlash } from 'react-icons/fa';
import { ImSpinner2 } from "react-icons/im";
import { FiCheck, FiUser, FiCalendar, FiMail, FiMapPin, FiHome, FiMessageSquare, FiAlertCircle, FiChevronLeft, FiPhone } from "react-icons/fi";
import styles from "../styles/account.module.css";

const REQUIRED_FIELDS = ["name", "mobile", "street"];

const Account = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [account, setAccount] = useState({
    name: "",
    birthdate: "",
    mobile: "",
    email: "",
    district: "",
    street: "",
    note: "",
    is_admin: ""
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [softLoading, setSoftLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const initialData = {
          name: user.name || "",
          birthdate: user.birthdate || "",
          mobile: user.mobile || "",
          email: user.email || "",
          district: user.district || "",
          street: user.street || "",
          note: user.note || "",
          is_admin: user.is_admin || ""
        };

        setAccount(initialData);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAccount();
  }, [user]);

  useEffect(() => {
    if (location.state?.fromCart) {
      setShowMessage(true);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const completion = useMemo(() => {
    const tracked = ["name", "birthdate", "mobile", "email", "district", "street"];
    const filled = tracked.filter((key) => account[key] && `${account[key]}`.trim().length > 0).length;
    return Math.round((filled / tracked.length) * 100);
  }, [account]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setAccount({ ...account, [name]: value});
  };

  const handleSave = async () => {
    const cleanAccount = Object.fromEntries(
      Object.entries(account).map(([name, value]) => [name, (name === "is_admin" || name === "birthdate") ? value : value.trim().replace(/\s+/g, ' ')])
    );

    if (REQUIRED_FIELDS.some((field) => !cleanAccount[field])) {
      toast.error(<span className="toast">قم بملىء البيانات المطلوبة</span>);

    } else {
      setSaveLoading(true);
      try {
        await dispatch(accountUpdate(cleanAccount)).unwrap;
        toast.success(<span className="toast">تم تحديث بياناتك بنجاح</span>);
      } catch (error) {
        toast.error(<span className="toast">لم نتمكن من حفظ التغييرات. حاول مرة اخرى</span>);
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleSoftDelete = async () => {
    confirmAlert({
      title: 'إلغاء الحساب ؟',
      message: `بإمكانك استرجاع حسابك خلال 30 يوم من تاريخ الإلغاء`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setSoftLoading(true);
            try {
              await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/soft-delete`, {}, { withCredentials: true });
              toast.success(<span className="toast">تم إلغاء حسابك بنجاح</span>, {
                onClose: () => {
                  dispatch(logout()).unwrap();
                  navigate("/");
                },
                autoClose: 1000,
              });
            } catch (error) {
              toast.error(<span className="toast">لم نتمكن من إلغاء الحساب. حاول مرة اخرى</span>);
            } finally {
              setSoftLoading(false);
            }
          }
        },
        {
          label: 'لا'
        }
      ]
    })
  };

  const initial = account.name ? account.name.trim().charAt(0) : "؟";

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.avatar}>{initial}</div>
        <div className={styles.heroText}>
          <h4>حســــابك</h4>
          <p>إدارة معلوماتك الشخصية وتفاصيل التوصيل بكل سهولة</p>
        </div>
      </div>

      <div className={styles.progressWrap}>
        <div className={styles.progressLabel}>
          <span>اكتمال الملف الشخصي</span>
          <span>{completion}%</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${completion}%` }} />
        </div>
      </div>

      {showMessage && (
        <div className={styles.alert}>
          <FiAlertCircle className={styles.alertIcon} />
          <p>قم بملىء البيانات المطلوبة حتى تتمكن من عمل الطلب</p>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><FiUser /></span>
          <h5>المعلومات الشخصية</h5>
        </div>

        <div className={styles.grid}>
          <div className={styles.field}>
            <label>الاسم <span className={styles.required}>*</span></label>
            <div className={styles.inputWrap}>
              <FiUser className={styles.inputIcon} />
              <input type="text" name="name" maxLength={30} value={account.name} onChange={handleChange} required />
            </div>
          </div>

          <div className={styles.field}>
            <label>تاريخ الميلاد</label>
            <div className={styles.inputWrap}>
              <FiCalendar className={styles.inputIcon} />
              <DatePicker
                selected={account.birthdate ? new Date(account.birthdate) : null}
                onChange={(date) => {
                  setAccount(prev => ({ ...prev, birthdate: date ? format(date, 'yyyy-MM-dd') : "" }));
                }}
                dateFormat="dd/MM/yyyy"
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                isClearable
                placeholderText="اختياري"
                className={styles.date}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label>رقم الهاتف <span className={styles.required}>*</span></label>
            <div className={`${styles.inputWrap} ${styles.ltr} ${!account.mobile ? styles.invalid : ''}`}>
              <FiPhone className={styles.inputIcon} />
              <input type="tel" name="mobile" maxLength={20} value={account.mobile} onChange={handleChange} required />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><FiMapPin /></span>
          <h5>التواصل و التوصيل</h5>
        </div>

        <div className={styles.grid}>
          <div className={styles.field}>
            <label>البريد الالكترونى</label>
            <div className={`${styles.inputWrap} ${styles.ltr}`}>
              <FiMail className={styles.inputIcon} />
              <input type="email" name="email" maxLength={100} value={account.email} readOnly />
            </div>
          </div>

          <div className={styles.field}>
            <label>المنطقة</label>
            <div className={styles.inputWrap}>
              <FiMapPin className={styles.inputIcon} />
              <input type="text" name="district" maxLength={30} value={account.district || ""} onChange={handleChange} />
            </div>
          </div>

          <div className={`${styles.field} ${styles.full}`}>
            <label>الشارع <span className={styles.required}>*</span></label>
            <div className={`${styles.inputWrap} ${styles.multiline}`}>
              <FiHome className={styles.inputIcon} />
              <textarea name="street" maxLength={255} value={account.street || ""} onChange={handleChange} required />
            </div>
          </div>

          <div className={`${styles.field} ${styles.full}`}>
            <label>ملاحظة</label>
            <div className={`${styles.inputWrap} ${styles.multiline}`}>
              <FiMessageSquare className={styles.inputIcon} />
              <textarea name="note" maxLength={255} value={account.note || ""} onChange={handleChange} />
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saveLoading || softLoading}
          className={`buttonNoBorder ${styles.saveBtn}`}
        >
          {saveLoading ? <ImSpinner2 className="smallspinner" /> :
            <FiCheck className="checkmark" />
          } تحديث البيانات
        </button>
      </div>

      <div className={styles.actionsRow}>
        <button
          onClick={() => navigate("/forgotpass")}
          disabled={saveLoading || softLoading}
          className={styles.actionBtn}
        >
          <span className={styles.actionIconWrap}><FaKey className={styles.actionIcon} /></span>
          تغيير كلمة المرور
          <FiChevronLeft className={styles.actionArrow} />
        </button>

        <button
          onClick={handleSoftDelete}
          disabled={softLoading || saveLoading}
          className={`${styles.actionBtn} ${styles.dangerAction}`}
        >
          {softLoading ? <ImSpinner2 className="smallspinner" /> : (
            <>
              <span className={styles.actionIconWrap}><FaUserSlash className={styles.actionIcon} /></span>
              إلغاء الحساب
              <FiChevronLeft className={styles.actionArrow} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Account;
